'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ElevatorAnimation, { FloatingCharacter } from './ElevatorAnimation';

interface HeroSectionProps {
  isHydrated: boolean;
}

export default function HeroSection({ isHydrated }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center">
      {/* Parallax Background Element */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-muted/20 to-transparent pointer-events-none"
        style={{
          transform: 'translateY(0)',
          transition: 'transform 0.1s ease-out',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Animated Logo with Elevator Animations */}
          <div
            className="relative flex justify-center items-center mb-8"
            style={{
              opacity: isHydrated ? 1 : 0,
              transform: isHydrated ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            {/* Left Elevator Animation */}
            {isHydrated && (
              <div className="absolute left-[-15%] top-0 bottom-0 w-24 hidden lg:block z-30">
                <ElevatorAnimation side="left" />
              </div>
            )}

            <AppImage
              src="/assets/images/tmpp6j64rno-1763965371090.jpg"
              alt="CR Elevator logo with red geometric frames containing black directional arrows, CR text in red, and ELEVATOR text in black, designed for transparent background usage"
              width={600}
              height={200}
              className="relative z-20 w-auto h-auto max-w-full"
              style={{
                maxHeight: 'clamp(8rem, 20vw, 16rem)',
                width: 'auto',
                objectFit: 'contain',
              }}
              priority
            />

            {/* Right Elevator Animation */}
            {isHydrated && (
              <div className="absolute right-[-15%] top-0 bottom-0 w-24 hidden lg:block z-30">
                <ElevatorAnimation side="right" />
              </div>
            )}

            {/* Floating Character Animation */}
            {isHydrated && (
              <FloatingCharacter />
            )}
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight leading-tight">
            Precision Parts. Zero Downtime.
          </h1>
          <p className="text-lg md:text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
            Elevate your vertical transportation projects with our QuickShip inventory — install in days, not weeks.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              href="/products"
              className="bg-red-600 text-white px-8 py-4 rounded-none font-bold text-lg hover:bg-red-700 transition-colors inline-block"
            >
              Explore Products
            </Link>
            <Link
              href="/contact"
              className="bg-white text-gray-900 px-8 py-4 rounded-none font-bold text-lg hover:bg-gray-100 transition-colors inline-block border-2 border-gray-900"
            >
              Get a Quote in Minutes
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}